import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LayerConfigMap } from '@/views/map/layers/types';
import { DEFAULT_MAP_LAYER_CONFIG, LayerType } from '@/views/map/layers/types';
import { GroupAnalysisType } from '@/generated/types/navigator/types.gen';

const initialState = DEFAULT_MAP_LAYER_CONFIG;

const DEFAULT_ANALYSIS_CONFIGS: Record<GroupAnalysisType, LayerConfigMap> = {
  [GroupAnalysisType.ENCROACHMENT]: {
    ...DEFAULT_MAP_LAYER_CONFIG,
  },
  [GroupAnalysisType.PRIORITY]: {
    ...DEFAULT_MAP_LAYER_CONFIG,
  },
  [GroupAnalysisType.WILDFIRE_PRIORITY]: {
    ...DEFAULT_MAP_LAYER_CONFIG,
    [LayerType.trees]: {
      visible: true,
      showHazardTrees: true,
      showDecliningTrees: false,
      showStrikeTrees: false,
      isHazardTreesOnly: false,
      showHeatmap: false,
    },
  },
  [GroupAnalysisType.FALL_IN]: {
    ...DEFAULT_MAP_LAYER_CONFIG,
    [LayerType.encroachment]: { visible: false },
    [LayerType.trees]: {
      visible: true,
      showHazardTrees: true,
      showDecliningTrees: true,
      showStrikeTrees: true,
      isHazardTreesOnly: false,
      showHeatmap: true,
    },
  },
  [GroupAnalysisType.CLEAR]: {
    ...DEFAULT_MAP_LAYER_CONFIG,
    [LayerType.trees]: {
      visible: true,
      showHazardTrees: true,
      showDecliningTrees: false,
      showStrikeTrees: true,
      isHazardTreesOnly: false,
      showHeatmap: false,
    },
  },
  [GroupAnalysisType.WORKLOAD_ANALYSIS]: {
    ...DEFAULT_MAP_LAYER_CONFIG,
    [LayerType.scan]: { visible: true },
    [LayerType.trees]: {
      visible: true,
      showHazardTrees: false,
      showDecliningTrees: false,
      showStrikeTrees: true,
      isHazardTreesOnly: false,
      showHeatmap: false,
    },
    [LayerType.critical_trees]: {
      visible: true,
    },
  },
};

const mapLayerSlice = createSlice({
  name: 'mapLayer',
  initialState,
  reducers: {
    update: (state, action) => {
      const layersToUpdate = Object.keys(action.payload).map(
        (i) => parseInt(i) as LayerType
      );
      const updatedLayerConfig: Partial<LayerConfigMap> = {};

      // Only update properties that have changed
      layersToUpdate.forEach(
        (l: LayerType) =>
          (updatedLayerConfig[l] = {
            ...state[l],
            ...action.payload[l],
          })
      );

      return {
        ...state,
        ...updatedLayerConfig,
      };
    },
    resetToAnalysisDefault: {
      reducer: (state, action: PayloadAction<LayerConfigMap>) => {
        return {
          ...state,
          ...action.payload,
        };
      },
      prepare: (
        selectedAnalysis: GroupAnalysisType,
        additionalState: Partial<LayerConfigMap> = {}
      ) => {
        return {
          payload: {
            ...DEFAULT_ANALYSIS_CONFIGS[selectedAnalysis],
            ...additionalState,
          },
          meta: null,
          error: null,
        };
      },
    },
  },
});

export const { update, resetToAnalysisDefault } = mapLayerSlice.actions;
export default mapLayerSlice.reducer;
